<script setup lang="ts">
import mapPinIcon from '@/assets/img/icon/map-pin.svg';
import {Loader} from '@googlemaps/js-api-loader';
import {debounce} from 'lodash';

interface LocationOption {
  place_id: string;
  full_name: string;
  description?: string;
  formatted_address?: string;
}

interface Props {
  label?: string;
  placeholder?: string;
  errorMessages?: string | string[];
  variant?: 'outlined' | 'plain' | 'filled' | 'underlined' | 'solo';
  density?: 'default' | 'comfortable' | 'compact';
  hideDetails?: boolean;
  prependInnerIcon?: string;
  rounded?: string | number | boolean;
  bgColor?: string;
  disabled?: boolean;
  bordered?: boolean;
  hidePrependIcon?: boolean;
  required?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  label: 'Location',
  placeholder: 'Enter location',
  variant: 'solo',
  density: 'default',
  hideDetails: false,
  prependInnerIcon: '',
  rounded: 'lg',
  bgColor: 'white',
  disabled: false,
  bordered: false,
  hidePrependIcon: false,
  required: false,
});

const location = defineModel<{ place_id?: string; formatted_address?: string; description?: string }>({
  default: {description: ''},
});

const {$api} = useNuxtApp();
const config = useRuntimeConfig();

const query = ref('');
const places = ref<LocationOption[]>([]);
const loading = ref(false);
const useGooglePlaces = ref(false);
const googlePlacesService = ref();
const sessionToken = ref();
const menuOpen = ref(false);
const userHasTyped = ref(false);
const isUpdatingFromModel = ref(false);
const requestErrorMessages = ref<string[]>([]);

// Function to search and auto-select first place (for initial load)
const searchAndSelectFirst = async (searchQuery: string) => {
  if (!searchQuery || searchQuery.trim() === '') return;

  try {
    const response = await $api<{ data: LocationOption[] }>('/places', {
      params: {input: searchQuery},
    });

    const results = response.data || [];
    if (results.length > 0) {
      const firstResult = results[0];
      location.value = {
        place_id: firstResult.place_id,
        formatted_address: firstResult.full_name,
        description: firstResult.full_name,
      };
      query.value = firstResult.full_name;
    } else {
      query.value = searchQuery;
    }
  } catch (error) {
    console.warn('Failed to fetch place for initial load, using query as-is');
    query.value = searchQuery;
  }
};

// Watch for model value changes from parent
watch(
    () => location.value,
    (newVal) => {
      if (newVal?.description) {
        // If there's no place_id (initial load from database), search and select first result
        if (!newVal.place_id) {
          searchAndSelectFirst(newVal.description);
        } else {
          // Already has place_id, just update the query
          isUpdatingFromModel.value = true;
          query.value = newVal.description;
          nextTick(() => {
            isUpdatingFromModel.value = false;
          });
        }
      }
    },
    {immediate: true}
);

// Initialize Google Places as fallback
const initializeGooglePlaces = async () => {
  if (!config.public.googleApiKey || useGooglePlaces.value) return;

  try {
    const loader = new Loader({
      apiKey: config.public.googleApiKey,
      version: 'weekly',
      libraries: ['places'],
    });

    const {AutocompleteService, AutocompleteSessionToken} = await loader.importLibrary('places');
    googlePlacesService.value = new AutocompleteService();
    sessionToken.value = new AutocompleteSessionToken();
    useGooglePlaces.value = true;
    console.log('Google Places initialized as fallback');
  } catch (error) {
    console.warn('Failed to initialize Google Places:', error);
  }
};

// Search using Google Places API
const searchGooglePlaces = async (searchQuery: string) => {
  if (!googlePlacesService.value || !sessionToken.value) return;

  try {
    const {predictions} = await googlePlacesService.value.getPlacePredictions({
      input: searchQuery,
      sessionToken: sessionToken.value,
      types: ['address'],
    });

    if (predictions) {
      places.value = predictions.map((prediction: any) => ({
        place_id: prediction.place_id,
        full_name: prediction.description,
        description: prediction.description,
        formatted_address: prediction.description,
      }));
    }
  } catch (error) {
    console.error('Google Places search failed:', error);
    places.value = [];
  }
};

// Debounced search function
const searchPlaces = debounce(async (searchQuery: string) => {
  if (!searchQuery || searchQuery.trim() === '') {
    places.value = [];
    return;
  }

  loading.value = true;
  requestErrorMessages.value = []

  try {
    // Try API first
    const response = await $api<{ data: LocationOption[] }>('/places', {
      params: {
        input: searchQuery,
      },
    });

    // Map full_name to description for compatibility
    places.value = (response.data || []).map((place) => ({
      ...place,
      description: place.full_name,
      formatted_address: place.full_name,
    }));

    if (places.value.length < 1) {
      requestErrorMessages.value = ['No locations found.'];
    }

  } catch (error: any) {
    requestErrorMessages.value = ['Failed to fetch places from API'];

    // Handle specific error cases
    if (error?.status === 403) {
      requestErrorMessages.value = ['Authentication required for places API'];
    } else if (error?.status === 500) {
      requestErrorMessages.value = ['Places API server error'];
    } else {
      requestErrorMessages.value = ['Unexpected error fetching places'];
    }

    // Try Google Places as fallback
    if (!useGooglePlaces.value) {
      await initializeGooglePlaces();
    }

    if (useGooglePlaces.value) {
      await searchGooglePlaces(searchQuery);
    } else {
      places.value = [];
    }
  } finally {
    loading.value = false;
  }
}, 500);

// Watch query changes
watch(query, (newQuery) => {
  // Skip search if the query was updated from model changes (not user typing)
  if (isUpdatingFromModel.value) {
    return;
  }

  if (newQuery && newQuery.trim() !== '') {
    userHasTyped.value = true;
    searchPlaces(newQuery);
  } else {
    userHasTyped.value = false;
    menuOpen.value = false;
    places.value = [];
  }
});

// Control menu visibility based on data availability and user interaction
watch([places, userHasTyped, loading], () => {
  // Show menu when user has typed AND (there are results OR currently loading OR finished loading with no results)
  if (userHasTyped.value && query.value && query.value.trim() !== '') {
    menuOpen.value = true;
  } else {
    menuOpen.value = false;
  }
});

// Handle focus event - prevent menu from showing if no query
const handleFocus = () => {
  if (!query.value || query.value.trim() === '') {
    menuOpen.value = false;
    userHasTyped.value = false;
  }
};

// Handle blur event
const handleBlur = () => {
  // Allow some time for selection to complete before closing
  setTimeout(() => {
    if (!query.value || query.value.trim() === '') {
      menuOpen.value = false;
      userHasTyped.value = false;
    }
  }, 200);
};

// Handle selection
const handleSelect = (selected: LocationOption | null) => {
  if (!selected) {
    location.value = {description: ''};
    query.value = '';
    userHasTyped.value = false;
    menuOpen.value = false;
    return;
  }

  location.value = {
    place_id: selected.place_id,
    formatted_address: selected.full_name || selected.formatted_address || selected.description || '',
    description: selected.full_name || selected.description || '',
  };
  userHasTyped.value = false;
  menuOpen.value = false;
};

// Computed selected item for v-autocomplete
const selectedItem = computed(() => {
  if (!location.value?.place_id) return null;
  return {
    place_id: location.value.place_id,
    description: location.value.formatted_address || location.value.description,
  };
});

const mergedErrorMessages = computed(() => {
  const base = props.errorMessages
      ? Array.isArray(props.errorMessages)
          ? props.errorMessages
          : [props.errorMessages]
      : []

  return [...base, ...(requestErrorMessages.value ?? [])]
})
</script>

<template>
  <div :class="{ bordered }">
    <v-autocomplete
        :model-value="selectedItem"
        :search="query"
        :items="places"
        item-title="description"
        item-value="place_id"
        :placeholder="placeholder"
        :variant="variant"
        :density="density"
        :hide-details="hideDetails"
        :loading="loading"
        :error-messages="mergedErrorMessages"
        :disabled="disabled"
        :rounded="rounded"
        :bg-color="bgColor"
        return-object
        no-filter
        clearable
        :hide-no-data="loading"
        @update:search="query = $event"
        @update:model-value="handleSelect"
        :menu-props="{ offset: 24 }"
        no-data-text="No locations found"
        class="location-autocomplete"
    >
      <template v-if="label" #label> {{ label }}<span v-if="required" class="text-error"> *</span></template>
      <template v-if="!hidePrependIcon" #prepend-inner>
        <img :src="mapPinIcon" alt="Location" class="map-pin-icon"/>
      </template>
    </v-autocomplete>
  </div>
</template>

<style scoped>
:deep(.v-label) {
  color: #414156 !important;
  opacity: 1 !important;
}

.map-pin-icon {
  width: 24px;
  height: 24px;
}

.location-autocomplete :deep(.v-field) {
  background-color: transparent !important;
}

.location-autocomplete :deep(.v-field--focused) {
  border-color: #414156 !important;
  background-color: transparent !important;
}

.location-autocomplete :deep(.v-field--focused .v-field__outline) {
  border-color: #414156 !important;
}

.bordered :deep(.v-input__control) {
  overflow: visible;
}

.bordered :deep(.v-field) {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  box-shadow: none !important;
  overflow: visible;
}

.bordered :deep(.v-field--focused) {
  border-color: rgb(var(--v-theme-primary));
}

.bordered :deep(.v-field--error) {
  border-color: rgb(var(--v-theme-error)) !important;
}

.bordered :deep(.v-field--error .v-field__outline),
.bordered :deep(.v-field__outline) {
  display: none;
}

.bordered :deep(.v-field__overlay) {
  display: none;
}
</style>
